// web/src/services/imageEditor.ts
// Canvas-based non-destructive image manipulation engine.
// All edits are applied to ImageData from the original — never mutating the source.

export interface EditState {
  brightness: number;   // -100 to 100
  contrast: number;     // -100 to 100
  saturation: number;   // -100 to 100
  warmth: number;       // -100 to 100
  sharpness: number;    // 0 to 100
  vignette: number;     // 0 to 100
  crop: CropRect | null;
  rotation: 0 | 90 | 180 | 270;
}

export interface CropRect {
  x: number; y: number; w: number; h: number;
}

export const DEFAULT_EDIT_STATE: EditState = {
  brightness: 0, contrast: 0, saturation: 0, warmth: 0,
  sharpness: 0, vignette: 0, crop: null, rotation: 0,
};

export function isDefaultState(state: EditState): boolean {
  return state.brightness === 0 && state.contrast === 0 &&
    state.saturation === 0 && state.warmth === 0 &&
    state.sharpness === 0 && state.vignette === 0 &&
    state.crop === null && state.rotation === 0;
}

/** Apply all edits to a canvas context from an original ImageData source. */
export function applyEdits(
  canvas: HTMLCanvasElement,
  original: ImageData,
  state: EditState,
): void {
  const w = original.width;
  const h = original.height;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  // Clone pixel data
  const data = new Uint8ClampedArray(original.data);

  // Per-pixel: brightness, contrast, saturation, warmth
  const bFactor = state.brightness * 2.55; // map -100..100 to -255..255
  const cFactor = (100 + state.contrast) / 100;
  const sFactor = (100 + state.saturation) / 100;
  const wShift = state.warmth * 0.5; // map -100..100 to -50..50

  for (let i = 0; i < data.length; i += 4) {
    let r = data[i], g = data[i + 1], b = data[i + 2];

    // Brightness
    r += bFactor; g += bFactor; b += bFactor;

    // Contrast (around midpoint 128)
    r = (r - 128) * cFactor + 128;
    g = (g - 128) * cFactor + 128;
    b = (b - 128) * cFactor + 128;

    // Warmth (shift R/B channels)
    r += wShift;
    b -= wShift;

    // Saturation (HSL-based)
    if (state.saturation !== 0) {
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      r = lum + (r - lum) * sFactor;
      g = lum + (g - lum) * sFactor;
      b = lum + (b - lum) * sFactor;
    }

    data[i] = Math.max(0, Math.min(255, r));
    data[i + 1] = Math.max(0, Math.min(255, g));
    data[i + 2] = Math.max(0, Math.min(255, b));
  }

  // Sharpness (unsharp mask)
  if (state.sharpness > 0) {
    applyUnsharpMask(data, w, h, state.sharpness / 100);
  }

  // Vignette
  if (state.vignette > 0) {
    applyVignette(data, w, h, state.vignette / 100);
  }

  // Put modified data
  const edited = new ImageData(data, w, h);
  ctx.putImageData(edited, 0, 0);

  // Rotation
  if (state.rotation !== 0) {
    const tempCanvas = document.createElement('canvas');
    const isSwap = state.rotation === 90 || state.rotation === 270;
    tempCanvas.width = isSwap ? h : w;
    tempCanvas.height = isSwap ? w : h;
    const tCtx = tempCanvas.getContext('2d')!;
    tCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
    tCtx.rotate((state.rotation * Math.PI) / 180);
    tCtx.drawImage(canvas, -w / 2, -h / 2);
    canvas.width = tempCanvas.width;
    canvas.height = tempCanvas.height;
    ctx.drawImage(tempCanvas, 0, 0);
  }

  // Crop
  if (state.crop) {
    const { x, y, w: cw, h: ch } = state.crop;
    const cropped = ctx.getImageData(x, y, cw, ch);
    canvas.width = cw;
    canvas.height = ch;
    ctx.putImageData(cropped, 0, 0);
  }
}

function applyUnsharpMask(data: Uint8ClampedArray, w: number, h: number, amount: number): void {
  // Simple 3x3 box blur then subtract for sharpening
  const blurred = new Float32Array(data.length);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      for (let c = 0; c < 3; c++) {
        let sum = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            sum += data[((y + dy) * w + (x + dx)) * 4 + c];
          }
        }
        blurred[(y * w + x) * 4 + c] = sum / 9;
      }
    }
  }
  const strength = amount * 2;
  for (let i = 0; i < data.length; i += 4) {
    for (let c = 0; c < 3; c++) {
      const sharp = data[i + c] + (data[i + c] - blurred[i + c]) * strength;
      data[i + c] = Math.max(0, Math.min(255, sharp));
    }
  }
}

function applyVignette(data: Uint8ClampedArray, w: number, h: number, amount: number): void {
  const cx = w / 2, cy = h / 2;
  const maxDist = Math.sqrt(cx * cx + cy * cy);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
      const ratio = dist / maxDist;
      // Start darkening at 50% from center, full effect at edges
      const darken = Math.max(0, (ratio - 0.5) * 2) * amount;
      const factor = 1 - darken * 0.7;
      const idx = (y * w + x) * 4;
      data[idx] *= factor;
      data[idx + 1] *= factor;
      data[idx + 2] *= factor;
    }
  }
}

/** Export canvas as a downloadable blob. Optionally add watermark. */
export async function exportCanvas(
  canvas: HTMLCanvasElement,
  watermark: boolean,
): Promise<Blob> {
  if (watermark) {
    const ctx = canvas.getContext('2d')!;
    const fontSize = Math.max(14, Math.round(canvas.width * 0.03));
    ctx.save();
    ctx.font = `${fontSize}px Inter, system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('FRAME', canvas.width - 12, canvas.height - 8);
    ctx.restore();
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to export canvas'));
    }, 'image/jpeg', 0.92);
  });
}

/** Load an image file into an ImageData object. */
export async function loadImageData(file: File): Promise<{ imageData: ImageData; width: number; height: number }> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = url;
  });
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  URL.revokeObjectURL(url);
  return {
    imageData: ctx.getImageData(0, 0, canvas.width, canvas.height),
    width: canvas.width,
    height: canvas.height,
  };
}

/** Map a dimension deficit to a concrete EditState adjustment. */
export function recipeToEditAdjustment(
  editName: string,
  intensity: number,
): Partial<EditState> {
  const scale = intensity * 100;
  switch (editName) {
    case 'Increase exposure': return { brightness: Math.round(scale * 0.3) };
    case 'Decrease exposure': return { brightness: -Math.round(scale * 0.3) };
    case 'Boost contrast': return { contrast: Math.round(scale * 0.4) };
    case 'Warm color temperature': return { warmth: Math.round(scale * 0.3) };
    case 'Cool color temperature': return { warmth: -Math.round(scale * 0.3) };
    case 'Increase saturation': return { saturation: Math.round(scale * 0.3) };
    case 'Decrease saturation': return { saturation: -Math.round(scale * 0.3) };
    case 'Add sharpness': return { sharpness: Math.round(scale * 0.5) };
    case 'Add vignette': return { vignette: Math.round(scale * 0.4) };
    default: return {};
  }
}
